/**
 * A HIÁNYLISTA — a könyvelő nyitóoldala, és a rendszer főképernyője (H1).
 *
 * A mérce a `docs/tervezes/design/MainBordo.dc.html` artboard: cégenkénti
 * csoportok, soronként szállító · számlaszám · összeg · állapot, alul a
 * várakozó sáv. A CSOPORTOSÍTÁS ÉS A RENDEZÉS A SZERVERRŐL JÖN KÉSZEN —
 * a bekérés cégenként egy levél (2.4d), tehát a csoport határa üzleti
 * fogalom, nem megjelenítés.
 *
 * MIÉRT `<table>` ÉS NEM `<div>`-RÁCS. Ez táblázatos adat: a sornak és az
 * oszlopnak jelentése van, és a képernyőolvasó a cellát a fejlécével együtt
 * mondja ki. A csoportfejléc `<th scope="rowgroup">` — NEM `colgroup`: a
 * `colgroup` a MARADÉK OSZLOPOKHOZ kötné a fejlécet, nem a `<tbody>` alatta
 * következő SORAIHOZ. A cég neve a sorok kontextusa, tehát `rowgroup`
 * (`akadalymentesites.md`). A `rowgroup` implicit szerepe `rowheader`.
 *
 * AMI ITT SZÁNDÉKOSAN NINCS:
 *   • jelölőnégyzet és a „Bekérés — N tétel" gomb. Az a 4–5. lépés: a
 *     kijelöléshez kiadás is tartozik, ahhoz sor (D7) és kimenő postaláda
 *     (2.6/21). Egy gomb, ami ma csak kijelöl, azt hazudná, hogy megy is.
 *   • a kinyitható „bizonyíték" panel. Az a bekérés melletti érv helye —
 *     a döntéshez tartozik, nem az olvasáshoz.
 *   • a „Gyanított" szakasz. A bank-ág az M2b; egy üres szakaszfejléc ma
 *     csak azt sugallná, hogy elromlott valami.
 *
 * SZÍNT A STÁTUSZ NEM KAP (`design-tokens.md`). A `WAITING` visszahúzott
 * háttérrel válik el, nem árnyalattal — az `--surface-recessed` pont ezért
 * van a tokenek közt.
 */

import { useTranslation } from 'react-i18next';
import { Caret, CashAccountingMark } from '@/components/common';
import { formatAmount, formatDate, formatTaxNumber, statusLabel } from '@/i18n/generated';
import type {
  DiscrepancyListGroup,
  DiscrepancyListItem,
  DiscrepancyListPartner,
  SkipReason,
} from './types';
import { companyRowId, type DiscrepancyListState, groupRows } from './useDiscrepancyList';
import type { DiscrepancySelectionState } from './useDiscrepancySelection';

/** Amit a `statusLabel` vár: sima kulcs → szöveg. */
type Translate = (key: string, options?: Record<string, unknown>) => string;

export function DiscrepancyList({
  state,
  selection,
}: {
  state: DiscrepancyListState;
  selection: DiscrepancySelectionState;
}) {
  const { t } = useTranslation();

  if (state.status === 'loading') {
    return <p className="hint">{t('discrepancyList.loading')}</p>;
  }

  if (state.status === 'forbidden') {
    // AZ ÚJRAPRÓBÁLÁS ITT NEM SEGÍT, ezért nincs is gomb. A szerepkör a friss
    // adatbázisból dolgozik: ha valakit lefokoztak, az azonnal hat, és nem
    // fog magától visszaváltozni. Emberhez kell fordulnia.
    return (
      <p className="notice" role="alert">
        {t('discrepancyList.forbidden')}
      </p>
    );
  }

  if (state.status === 'failed' || !state.list) {
    // A HIBÁT KIMONDJUK, ÉS AZT IS, MIT NEM JELENT. Egy üres lista azt
    // állítaná, hogy nincs bekérhető hiány — az a 2.4d „hamis nulla"
    // bukásmódja: a könyvelő nem tudná, hogy nem hisz-e el valamit.
    //
    // A gomb azért van itt, mert ez az ág ÁTMENETI lehet, és egy zsákutca-
    // képernyőn a következő lépés az oldal újratöltése lenne — az pedig
    // kidobná a fókuszt és a cégválasztást is.
    return (
      <div className="notice" role="alert">
        <p>{t('discrepancyList.loadFailed')}</p>
        <button type="button" onClick={state.reload}>
          {t('discrepancyList.retry')}
        </button>
      </div>
    );
  }

  const { groups, waiting, totals } = state.list;

  return (
    <>
      <p className="totals">
        {t('discrepancyList.itemCount', { count: totals.itemCount })}
        {' · '}
        {/* ⚠️ A `vatAtRisk` SZÁNDÉKOSAN FORINTBAN MARAD, pedig a sorok
            összege pénznemet kapott (#39). Ez MAGYAR ÁFA — a tét mindig
            forintban áll, akkor is, ha a számla EUR-ban jött —, ráadásul
            összeg cégek felett: egy pénznem-jelölés itt azt hazudná, hogy
            devizánként külön mérünk. Ne „javítsd" a sorokkal egyformára. */}
        {t('discrepancyList.totals.deductible')}: {formatAmount(Number(totals.deductible))} ·{' '}
        {t('discrepancyList.totals.payable')}: {formatAmount(Number(totals.payable))}
      </p>

      <RequestFeedback selection={selection} />

      {/* AZ ÜRES ÁLLAPOT A HIÁNYOK SZÁMÁRA HALLGAT, nem a csoportokéra.
          Egy cég, aminek MINDEN sora `WAITING`, csoportot ad `itemCount: 0`-val
          — ott a `groups.length` nem nulla, és a képernyő „0 tétel · 0 Ft"-ot
          meg egy üres „Bizonyított" szakaszt mutatna. Márpedig az a JÓ
          állapot: nincs mit bekérni. A várakozó tételek a sávban maradnak. */}
      {totals.itemCount === 0 ? (
        <p className="empty">{t('discrepancyList.empty')}</p>
      ) : (
        <>
          <h2 className="section">
            {t('discrepancyList.section.proven')}
            {/* A `--official` a HIVATALOS ADAT szerepe: a partner bejelentette
                a NAV-nak. Ez az, ami megnevezhető és bekérhető (2.5c). */}
            <span className="badge badge--official">
              {t('discrepancyList.section.provenSource')}
            </span>
          </h2>

          <table className="list">
            <thead>
              <tr>
                {/* A jelölőnégyzet oszlopának nincs FELIRATA — a soronkénti
                    `aria-label` mondja meg, mit jelölünk. Egy „Kijelölés"
                    oszlopfej minden sornál ismétlődne a képernyőolvasóban. */}
                <th scope="col" className="pick" />
                {/* ⚠️ IRÁNYSEMLEGES FEJLÉC (#51). Korábban „Szállító" állt itt,
                    de az oszlop MOSTANTÓL vegyes sorok fölött áll: bejövőnél a
                    kiállító, kimenőnél a vevő. Egy fejléc, ami az egyik oldalt
                    nevezi meg, a másik sorokra hazudik — ugyanaz az érvelés,
                    mint a `totals` iránybontásánál. Az irány SORONKÉNT látszik. */}
                {/* FA (#77, „B5"): partner › számla egy oszlopban. A számlaszám a
                    fába költözött, a külön oszlop megszűnt. A fejléc így is
                    IRÁNYSEMLEGES (#51): a szerep („Szállító" / „Vevő") az ágon áll. */}
                <th scope="col">{t('discrepancyList.column.partnerInvoice')}</th>
                <th scope="col" className="right">
                  {t('discrepancyList.column.amount')}
                </th>
                <th scope="col">{t('discrepancyList.column.status')}</th>
                {/* A művelet-oszlopnak sincs FELIRATA — a gomb maga mondja meg,
                    mit tesz, és a soronkénti `aria-label` a kivel. */}
                <th scope="col" className="row__action" />
              </tr>
            </thead>

            {groups.map((group) => (
              <CompanyGroup
                key={group.company.id}
                group={group}
                state={state}
                selection={selection}
              />
            ))}
          </table>
        </>
      )}

      {waiting.itemCount > 0 ? (
        <p className="waiting-bar">
          {t('discrepancyList.waitingBar', { count: waiting.itemCount })}
        </p>
      ) : null}
    </>
  );
}

/** Egy cég a hiánylistán — a bekérés egysége, ezért saját `<tbody>`. */
function CompanyGroup({
  group,
  state,
  selection,
}: {
  group: DiscrepancyListGroup;
  state: DiscrepancyListState;
  selection: DiscrepancySelectionState;
}) {
  const { t } = useTranslation();
  const picked = selection.selectedCountFor(group.company.id);
  const collapsed = state.isCollapsed(group.company.id);
  const rowId = companyRowId(group.company.id);
  const focused = state.focusedId === rowId;
  const pickable = selection.selectableCountFor(group.company.id);
  const mark = selection.selectionFor(group.company.id);
  // A csoport pénzforgalmi számláinak száma (#55) — hogy összecsukott
  // csoportnál se vesszen el a jelzés. A `WAITING` is beleszámít: a jelölés a
  // SZÁMLA tulajdonsága, nem a hiányé.
  const cashAccounting = group.items.filter((item) => item.cashAccounting).length;

  return (
    <tbody>
      {/* A FEJLÉC IS SOR A KURZORNAK (#49) — ugyanaz a roving tabindex, mint a
          tételeken. Enélkül egy összecsukott céget billentyűvel nem lehetne
          visszanyitni: nincs alatta látható sor, amiről a `L` szólhatna. */}
      <tr
        ref={(element) => state.registerRow(rowId, element)}
        tabIndex={focused ? 0 : -1}
        aria-current={focused ? true : undefined}
        onFocus={() => state.setFocusedId(rowId)}
        className={collapsed ? 'group group--collapsed' : 'group'}
      >
        {/* A BELSŐ TÖRDELÉS EGY `<div>`-EN MEGY, nem a cellán. A `display: flex`
            a `<th>`-ról LEVENNÉ a cella-szerepet, és vele a `colSpan`-t is: a
            fejléc az első oszlop szélességébe szorulna, a cégnév betűnként
            tördelve. A cella maradjon cella; a rács benne él. */}
        <th colSpan={5} scope="rowgroup">
          <div className="group__inner">
            {/* MINDET KI/BE ENNÉL A CÉGNÉL (#49). `tabIndex={-1}`: a
                billentyűzet útja a soron megy, a `Szóköz` váltja — ahogy a
                tételek négyzeténél is. Egérrel viszont kattintható.
                Kijelölhető tétel híján LE VAN TILTVA, nem csak üres. */}
            <input
              type="checkbox"
              className="group__pick"
              tabIndex={-1}
              disabled={pickable === 0}
              checked={mark === 'all'}
              ref={(element) => {
                // AZ `indeterminate` NEM ATTRIBÚTUM, csak tulajdonság — JSX-ből
                // nem adható meg. Enélkül a részleges kijelölés üresnek
                // látszana, és a következő kattintás kijelölne mindent
                // ahelyett, hogy levenné.
                if (element) {
                  element.indeterminate = mark === 'some';
                }
              }}
              onChange={() => selection.toggleCompany(group.company.id)}
              aria-label={t('discrepancyList.selectCompany', { company: group.company.name })}
            />
            {/* A CÉGNÉV A KAPCSOLÓ — nem egy külön nyíl-ikon mellette. A
                fejléc egésze egy dolgot csinál, és a legnagyobb célpont az,
                amire a könyvelő amúgy is néz. */}
            <button
              type="button"
              className="group__toggle"
              tabIndex={-1}
              aria-expanded={!collapsed}
              // A GOMB NEVE A CÉGNÉV, az állapotát az `aria-expanded` mondja —
              // ez a szokásos „disclosure" minta. A `title` az egérhasználóé:
              // ő nem hallja az `aria-expanded`-et.
              title={
                collapsed
                  ? t('discrepancyList.expandCompany', { company: group.company.name })
                  : t('discrepancyList.collapseCompany', { company: group.company.name })
              }
              onClick={() => state.setCollapsed(group.company.id)}
            >
              <Caret open={!collapsed} />
              {/* A FA GYÖKERE: az ügyfél (#77, „B5"). A címke mondja ki, hogy
                  ez a könyvelő ügyfele — a partnerek alatta szállítók, vevők. */}
              <span className="group__kind">{t('discrepancyList.clientLabel')}</span>
              <span className="group__name">{group.company.name}</span>
            </button>
            <span className="group__tax">{formatTaxNumber(group.company.taxNumber)}</span>
            {cashAccounting > 0 ? (
              <span className="group__cash">
                {t('discrepancyList.cashAccountingCount', { count: cashAccounting })}
              </span>
            ) : null}
            <span className="spacer" />
            <span>{t('discrepancyList.itemCount', { count: group.itemCount })}</span>
            {/* Forintban — lásd a `totals` fenti indoklását (#39). */}
            <span className="group__stake">{formatAmount(Number(group.vatAtRisk))}</span>
            {/* A BEKÉRÉS EGYSÉGE A CÉG (UC1 7. lépés) — ezért van a gomb a
                csoportfejlécen, és nem a soron. Soronként kiadva ugyanaz az
                ügyfél egy kör alatt több levelet kapna (2.4d). */}
            <button
              type="button"
              className="group__request"
              disabled={picked === 0 || selection.sending}
              onClick={() => selection.request(group.company.id)}
            >
              {t('discrepancyList.requestGroup', { count: picked })}
            </button>
          </div>
        </th>
      </tr>

      {/* AZ ÖSSZECSUKOTT CÉG TÉTELEI NEM RENDERELŐDNEK — nem `display: none`.
          Egy elrejtett sor a képernyőolvasónak és a `Tab`-nak is ott lenne,
          és a „becsuktam, mert végeztem vele" mozdulat nem csinálna semmit.
          A KIJELÖLÉS EZZEL NEM VÉSZ EL: az a `useDiscrepancySelection`-ben
          él, nem a DOM-ban — a fejléc gombja a becsukott cég kijelölt
          tételeit is viszi. */}
      {/* FA: ÜGYFÉL → PARTNER → SZÁMLA (#77, „B5"). A sorrendet és az ágakat a
          szerver adja; a `groupRows` csak kirakja őket — ugyanabból, amiből a
          kurzor sorrendje is jön. */}
      {collapsed
        ? null
        : groupRows(group).map((row) =>
            row.kind === 'partner' ? (
              <PartnerRow
                key={row.rowId}
                rowId={row.rowId}
                partner={row.partner}
                last={row.last}
                state={state}
                selection={selection}
              />
            ) : (
              <Row
                key={row.rowId}
                item={row.item}
                guides={leafGuides(row)}
                nested={row.nested}
                companyId={group.company.id}
                state={state}
                selection={selection}
              />
            )
          )}
    </tbody>
  );
}

/**
 * A FA VEZETŐI (#77, „B5") — sorról sorra, mélységenként egy. A vonalat CSS
 * rajzolja a cella teljes magasságában, így a szomszédos sorok vezetői
 * folyamatos ággá állnak össze:
 *
 *   `pipe`  — egy felsőbb ág itt még folytatódik
 *   `tee`   — elágazás, alatta van még testvér
 *   `elbow` — az utolsó testvér: a függőleges vonal itt véget ér
 *   `blank` — a felsőbb ág már véget ért, nincs mit húzni
 *
 * Díszítés, a képernyőolvasónak nem mond semmit — a szerkezetet a sorok
 * sorrendje és a jelölőnégyzetek címkéje adja.
 */
type Guide = 'pipe' | 'tee' | 'elbow' | 'blank';

/** A levél vezetői: a szülő ága folytatódik-e, és ő-e az utolsó a szülő alatt. */
function leafGuides(row: { nested: boolean; last: boolean; parentLast: boolean }): Guide[] {
  if (!row.nested) {
    return [];
  }
  return [row.parentLast ? 'blank' : 'pipe', row.last ? 'elbow' : 'tee'];
}

/**
 * ⚠️ A VEZETŐ ABSZOLÚT POZÍCIÓJÚ, a cella TELJES magasságában — nem egy fix
 * magasságú inline elem. A sor magasságát a legmagasabb cellája adja (a
 * „Bekérem" gomb, a pénzforgalmi jelölés), és egy fix vezető alatta-fölötte
 * rést hagyna: a fa vonala sorról sorra megszakadna. A jsdom CSS-t nem
 * futtat, ezt teszt nem fogja meg — a code review fogta meg (10-30).
 */
const GUIDE_WIDTH_REM = 1.375;
const CELL_PADDING_REM = 0.75;

function guideOffset(depth: number): string {
  return `${CELL_PADDING_REM + depth * GUIDE_WIDTH_REM}rem`;
}

function TreeGuides({ guides }: { guides: readonly Guide[] }) {
  return (
    <>
      {guides.map((guide, at) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: a vezetők sorrendje a mélység, nem változik
          key={at}
          className={`tree__guide tree__guide--${guide}`}
          style={{ left: guideOffset(at) }}
          aria-hidden="true"
        />
      ))}
    </>
  );
}

/**
 * A PARTNER ÁGA A CÉGEN BELÜL (#77, artboard: `design/hianylista-partner/`,
 * „B5"). Előtte a SZEREP („Szállító" / „Vevő") — enélkül a partner neve
 * egy második ügyfélnek látszott a cégfejléc alatt (Viktor, 10-30).
 *
 * ⚠️ NEM `<th scope="rowgroup">`, mint a cégé. A `rowgroup` a `<tbody>`-hoz
 * köt, és egy `<tbody>`-ban egy van belőle: a cégé. A partner neve itt egy
 * `rowheader`.
 *
 * ÖSSZECSUKNI NEM LEHET — szándékosan („legyen az összes számla egy
 * szinten"). A `H`/`L` a cégre hat, ahogy eddig.
 */
function PartnerRow({
  rowId,
  partner,
  last,
  state,
  selection,
}: {
  rowId: string;
  partner: DiscrepancyListPartner;
  last: boolean;
  state: DiscrepancyListState;
  selection: DiscrepancySelectionState;
}) {
  const { t } = useTranslation();
  const focused = state.focusedId === rowId;
  const mark = selection.selectionForIds(partner.itemIds);
  const open = partner.itemIds.filter((id) => selection.isSelectable(id)).length;
  const name = partner.name ?? t('discrepancyList.partnerless');

  return (
    <tr
      ref={(element) => state.registerRow(rowId, element)}
      tabIndex={focused ? 0 : -1}
      aria-current={focused ? true : undefined}
      onFocus={() => state.setFocusedId(rowId)}
      className="partner"
    >
      <td className="pick">
        <input
          type="checkbox"
          tabIndex={-1}
          disabled={open === 0}
          checked={mark === 'all'}
          ref={(element) => {
            // Lásd a cégfejlécet: az `indeterminate` csak tulajdonság.
            if (element) {
              element.indeterminate = mark === 'some';
            }
          }}
          onChange={() => selection.toggleIds(partner.itemIds)}
          aria-label={t('discrepancyList.selectPartner', { partner: name })}
        />
      </td>
      <th scope="row" className="tree" style={{ paddingLeft: guideOffset(1) }}>
        <TreeGuides guides={[last ? 'elbow' : 'tee']} />
        {partner.direction !== null && (
          <span className="partner__role">{t(`discrepancyList.role.${partner.direction}`)}</span>
        )}
        <span className="partner__name">{name}</span>
        <span className="partner__count">
          {t('discrepancyList.itemCount', { count: partner.itemIds.length })}
        </span>
      </th>
      {/* Forintban, mint a tételeké — lásd a `totals` indoklását (#39).
          A CSUPA VÁRAKOZÓ ÁG ÖSSZEGE ÜRES, nem „0 Ft": az ág összege a
          hiányoké, és egy szállító mellett a nulla nulla értékű számlának
          olvasódna, nem annak, hogy nincs mit bekérni. */}
      <td className="right">
        {open > 0 || Number(partner.amount) !== 0 ? formatAmount(Number(partner.amount)) : null}
      </td>
      <td className="status">
        {open > 0 ? t('discrepancyList.partnerOpen', { count: open }) : null}
      </td>
      <td className="row__action" />
    </tr>
  );
}

function Row({
  item,
  guides,
  nested,
  companyId,
  state,
  selection,
}: {
  item: DiscrepancyListItem;
  guides: readonly Guide[];
  /**
   * ÁG ALATT ÁLL-E (#77). Ilyenkor a sor a fa levele: a számlaszám áll rajta,
   * a partner neve és szerepe az ágon. Ha nem — régebbi szerverválasz —, a sor
   * a régi, lapos alakú: partner, irány, számlaszám egy cellában.
   */
  nested: boolean;
  /** A SOR CÉGE. A tétel maga nem hordozza — a csoport a kontextusa. */
  companyId: string;
  state: DiscrepancyListState;
  selection: DiscrepancySelectionState;
}) {
  const { t } = useTranslation();
  const focused = state.focusedId === item.id;
  const selectable = selection.isSelectable(item.id);

  return (
    // ROVING TABINDEX: a listából EGY sor érhető el `Tab`-bal, a többi közt a
    // ↑↓ visz. Enélkül nyolcvan cég tételein a `Tab` több száz megállót
    // jelentene — az a billentyűzet-első felület pontos ellentéte.
    <tr
      ref={(element) => state.registerRow(item.id, element)}
      tabIndex={focused ? 0 : -1}
      aria-current={focused ? true : undefined}
      onFocus={() => state.setFocusedId(item.id)}
      className={item.state === 'WAITING' ? 'row row--waiting' : 'row'}
    >
      <td className="pick">
        {/* `tabIndex={-1}`: a listából EGY sor érhető el `Tab`-bal, a
            jelölőnégyzetek nem külön megállók — nyolcvan cég tételein az
            több száz `Tab`-ot jelentene. A billentyűzet útja a soron megy,
            a `Szóköz` váltja. Egérrel viszont a négyzet is kattintható.
            Ami nem jelölhető ki, az LE VAN TILTVA, nem csak üres: a `WAITING`
            nem hiány, a `REQUESTED` pedig már kiment. */}
        <input
          type="checkbox"
          tabIndex={-1}
          disabled={!selectable}
          checked={selection.selected.has(item.id)}
          onChange={() => selection.toggle(item.id)}
          aria-label={t('discrepancyList.selectRow', {
            supplier: rowSubject(item),
            invoiceNumber: item.invoiceNumber ?? '—',
          })}
        />
      </td>
      <td className="tree" style={{ paddingLeft: guideOffset(guides.length) }}>
        <TreeGuides guides={guides} />
        {nested ? (
          // A PARTNER NEVE ÉS SZEREPE A KÉPERNYŐOLVASÓNAK MEGMARAD — a szemnek
          // az ág mondja ki fölötte. A szerep nem dísz (#51): ha ugyanaz a cég
          // szállító és vevő is, enélkül két ág levelei egyformán szólnának.
          item.partnerName !== null && (
            <span className="visually-hidden">
              {item.direction !== null && `${t(`discrepancyList.role.${item.direction}`)} `}
              {item.partnerName}{' '}
            </span>
          )
        ) : (
          <>
            {rowSubject(item)}
            {/* ⚠️ AZ IRÁNY A LAPOS SORON IS LÁTSZIK (#51) — a fában a szerep
                az ágon áll; régebbi szervernél ez az egyetlen hordozója. */}
            {item.direction !== null && (
              <span className="row__direction">{t(`document.direction.${item.direction}`)}</span>
            )}{' '}
          </>
        )}
        {/* A LEVÉL: a számlaszám — vagy ha nincs, az olvashatatlan papír
            fájlneve (#23). A lapos soron a fájlnév már a tárgyban áll. */}
        <span className="tree__leaf">
          {item.invoiceNumber ?? (nested ? (item.documentFilename ?? '—') : '—')}
        </span>
        {/* A PÉNZFORGALMI JELÖLÉS A SZÁMLASZÁM MÖGÖTT (#55, Viktor 2026-09-29,
            artboard: `design/penzforgalmi-jelzes/`). A számla tulajdonsága, ezért
            a számlánál áll. VALÓDI SZÓKÖZ előtte, nem csak margó: nélküle a
            cella szövege „AEK-2026/004182pénzforgalmi", és a képernyőolvasó egy
            szónak olvashatja. */}
        {item.cashAccounting ? (
          <>
            {' '}
            <CashAccountingMark />
          </>
        ) : null}
      </td>
      {/* Forintban — a NAV-tétel összege `netAmountHUF + vatAmountHUF`, a
          `currency` pedig a számla eredeti pénzneme. Lásd a `Worklist.tsx`
          jelölt-listájánál a teljes indoklást (#39). */}
      <td className="right">{item.amount === null ? '—' : formatAmount(Number(item.amount))}</td>
      {/* OSZTÁLYNÉVVEL, nem `:last-child`-dal: a művelet-oszlop mögé került,
          és egy helyzethez kötött szabály némán a gombra vándorolt volna. */}
      <td className="status">{statusText(t, item)}</td>
      {/* EGYENKÉNTI BEKÉRÉS (#49) — az UTOLSÓ oszlopban, a művelet helyén.
          `tabIndex={-1}`, mint a jelölőnégyzeté: a billentyűzet útja a soron
          megy (`Shift+Enter`), az egéré a gombon.
          CSAK A BEKÉRHETŐ SORON van gomb — a `WAITING` nem hiány, a
          `REQUESTED` már kiment; egy letiltott gomb minden soron csak zajt
          tenne a listára. */}
      <td className="row__action">
        {selectable ? (
          <button
            type="button"
            className="row__request"
            tabIndex={-1}
            disabled={selection.sending}
            onClick={() => selection.request(companyId, [item.id])}
            aria-label={t('discrepancyList.requestRowFor', {
              supplier: rowSubject(item),
              invoiceNumber: item.invoiceNumber ?? '—',
            })}
          >
            {t('discrepancyList.requestRow')}
          </button>
        ) : null}
      </td>
    </tr>
  );
}

/**
 * MIRŐL SZÓL A SOR — a partner, vagy ha nincs, a FÁJLNÉV.
 *
 * Az olvashatatlan bizonylatnál (#23) nincs NAV-tétel, tehát nincs partner,
 * számlaszám és összeg sem: a sor három gondolatjellel állna a listán, és a
 * könyvelő nem tudná megmondani, MELYIK papírról van szó. A fájlnév az, amit
 * az ügyfél is felismer — ő küldte be.
 *
 * ⚠️ A FELIRAT NEM VÁLTOZIK ETTŐL. Az továbbra is a `state` + `certainty`
 * párosból jön (`statusLabel`), tehát az olvashatatlan sor „Beazonosítatlan"
 * marad (Viktor, 2026-09-09). Ez a függvény a sor TÁRGYÁT adja, nem az
 * állapotát.
 */
function rowSubject(item: DiscrepancyListItem): string {
  return item.partnerName ?? item.documentFilename ?? '—';
}

/**
 * Az állapot felirata — a MEGOSZTOTT `statusLabel`-ből, plusz a türelmi óra
 * dátuma. A felirat maga sosem itt keletkezik: az értesítő levél ugyanazokat
 * az állapotokat írja le, és ha három helyen készülne, a termék három hangon
 * beszélne.
 */
function statusText(t: Translate, item: DiscrepancyListItem): string {
  // Kimondott szűkítés, nem kaszt: a `DiscrepancyStatus` unió tartja, hogy a
  // `WAITING` + `SUSPECTED` páros LÉTRE SEM JÖHET — a gyanítottnak nincs
  // türelmi ideje. Egy `as`-szal épp ezt az invariánst dobnánk el.
  const label = statusLabel(
    (key) => t(key),
    item.state === 'WAITING'
      ? { state: 'WAITING', certainty: 'PROVEN' }
      : { state: item.state, certainty: item.certainty }
  );

  if (item.chaseableFrom === null) {
    return label;
  }
  const date = formatDate(item.chaseableFrom);

  switch (item.state) {
    case 'WAITING':
      // Meddig tartjuk a feltevést, hogy a papír a feldolgozatlan kupacban van.
      return `${label} — ${t('discrepancyList.graceUntil', { date })}`;
    case 'OPEN':
      // Mióta bekérhető. Ez az, amiből a könyvelő látja, mi áll régóta.
      return `${label} ${t('discrepancyList.chaseableSince', { date })}`;
    default:
      // `REQUESTED` — a kiküldés időpontja NEM a türelmi óra. Az a
      // `Notification` sora lesz, a kimenő postaládával (4–5. lépés);
      // addig a `chaseableFrom`-ot kiírni itt hazugság volna.
      return label;
  }
}

/**
 * A kimaradás indoka — LITERÁL kulcsokkal, kimerítő `switch`-csel.
 *
 * SZÁNDÉKOSAN nem sablonkulccsal, az indokot a kulcs végére illesztve: az
 * i18n-kapu a literál alakot méri, egy összefűzött kulcsot pedig nem tud
 * feloldani. Egy elgépelt vagy lefordítatlan indok ott CSENDBEN nyers
 * kulcsot renderelne — és a kapu csak annyit mondana, hogy „nézz rá kézzel".
 * Ugyanez az érv áll a megosztott `statusLabel`-ben is, szó szerint.
 *
 * A `never`-ág ráadásul azt is megadja, hogy amikor a szerver új indokot
 * vezet be, a FORDÍTÓ mutat rá — nem egy éles képernyő.
 */
function skipReasonLabel(t: Translate, reason: SkipReason): string {
  switch (reason) {
    case 'ALREADY_REQUESTED':
      return t('discrepancyList.skipReason.ALREADY_REQUESTED');
    case 'RESOLVED':
      return t('discrepancyList.skipReason.RESOLVED');
    case 'DISMISSED':
      return t('discrepancyList.skipReason.DISMISSED');
    case 'NOT_CHASEABLE':
      return t('discrepancyList.skipReason.NOT_CHASEABLE');
    case 'SECOND_ROUND':
      return t('discrepancyList.skipReason.SECOND_ROUND');
    case 'NOT_REQUESTABLE_YET':
      return t('discrepancyList.skipReason.NOT_REQUESTABLE_YET');
    case 'RESCAN_NOT_CLEAN':
      return t('discrepancyList.skipReason.RESCAN_NOT_CLEAN');
    case 'NAV_NOT_CONNECTED':
      return t('discrepancyList.skipReason.NAV_NOT_CONNECTED');
    case 'SUPERSEDED':
      return t('discrepancyList.skipReason.SUPERSEDED');
    case 'RECONCILIATION_FAILED':
      return t('discrepancyList.skipReason.RECONCILIATION_FAILED');
    default: {
      // Kimerítőség-ellenőrzés: ha a `SkipReason` bővül, ez a sor nem fordul le.
      const exhaustive: never = reason;

      // ⚠️ FUTÁSIDŐBEN VISZONT NEM DOBUNK — ugyanaz az érvelés, mint a
      // `docTypeLabel`-nél, és ezt a commit maga bizonyítja. A backend és az
      // admin KÜLÖN DEPLOYOLHATÓ egység: a #23 két indoka (`SECOND_ROUND`,
      // `NOT_REQUESTABLE_YET`) hetekkel a felületi típus előtt ment ki, és
      // ebben az ablakban egy olvashatatlan tételre kiadott bekérés DOBOTT
      // volna itt. ErrorBoundary híján az a könyvelő FŐKÉPERNYŐJÉT vitte
      // volna el, egy feliratért.
      void exhaustive;
      return t('discrepancyList.skipReason.UNKNOWN');
    }
  }
}

/**
 * MIT MONDUNK A BEKÉRÉS UTÁN.
 *
 * „Bekérve", nem „elküldve". A szerver 202-t ad, és az azt jelenti, hogy egy
 * tranzakcióban megszületett az értesítés, a tételek `REQUESTED`-be léptek, és
 * a küldés bekerült a kimenő postaládába (2.6/21). Hogy a levél TÉNYLEG
 * kiment-e, az a kérés pillanatában még nem igaz — ezt állítani hazugság
 * lenne, méghozzá pont abban, amiért az egész csatorna értékes.
 *
 * A KIMARADT TÉTELEKET IS KIMONDJUK. Egy „sikerült" felirat öt tételre,
 * amiből kettő kimaradt, csendben veszítene el két bekérést.
 */
function RequestFeedback({ selection }: { selection: DiscrepancySelectionState }) {
  const { t } = useTranslation();

  if (selection.sending) {
    return <p className="hint">{t('discrepancyList.requestSending')}</p>;
  }

  const feedback = selection.feedback;
  if (!feedback) {
    return null;
  }

  switch (feedback.kind) {
    case 'done':
      return (
        <output className="notice notice--clear">
          <p>
            {t('discrepancyList.requestDone', {
              count: feedback.count,
              company: feedback.company,
            })}
          </p>
          {feedback.skipped.length > 0 ? (
            <p>
              {t('discrepancyList.requestSkipped', {
                count: feedback.skipped.length,
                reasons: [...new Set(feedback.skipped)]
                  .map((reason) => skipReasonLabel(t, reason))
                  .join(', '),
              })}
            </p>
          ) : null}
        </output>
      );
    case 'empty':
      return <output className="notice">{t('discrepancyList.requestEmpty')}</output>;
    case 'stale':
      return (
        <p className="notice" role="alert">
          {t('discrepancyList.requestStale')}
        </p>
      );
    case 'rejected':
      // A szerver üzenete a FELHASZNÁLÓNAK szól (400) — lásd a horog
      // `RequestFeedback` típusát. Itt mutatni pontosabb, mint helyettesíteni.
      return (
        <p className="notice" role="alert">
          {feedback.message}
        </p>
      );
    case 'forbidden':
      return (
        <p className="notice" role="alert">
          {t('discrepancyList.requestForbidden')}
        </p>
      );
    case 'failed':
      return (
        <p className="notice" role="alert">
          {t('discrepancyList.requestFailed')}
        </p>
      );
    default: {
      const exhaustive: never = feedback;
      throw new Error(`Ismeretlen visszajelzés: ${JSON.stringify(exhaustive)}`);
    }
  }
}
